import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { splitSceneCropKeyframes } from '@/lib/timeline/naturalScenes';
import { fillIntervalCropGaps } from './fillIntervalCropGaps';
import { resolveCropKeyframeAtTime } from './resolveCropKeyframeAtTime';

type SceneLike = { startTime: number; endTime: number };
type CropFraming = Pick<CropCenterKeyframe, 'x' | 'y' | 'zoom'>;

const overlapsScene = (keyframe: CropCenterKeyframe, scene: SceneLike) =>
  Math.min(keyframe.endTime, scene.endTime) > Math.max(keyframe.time, scene.startTime);

export interface SceneCropTarget {
  /** The keyframe framing the scene at the playhead (a stand-in when no
   *  keyframe covers the scene, holding the crop the preview shows there). */
  keyframe: CropCenterKeyframe;
  /** The scene under the playhead. */
  scene: SceneLike;
  /** The part of the scene that keyframe frames: what a crop edit changes. */
  start: number;
  end: number;
}

/**
 * What a crop edit at `time` should change: the scene under the playhead,
 * limited to the keyframe framing it. A keyframe is an auto-detected camera
 * shot, so it often spans several scenes of the clip; editing it whole moved
 * every one of them (2026-10-07 report). `null` outside the clip's scenes.
 */
export function resolveSceneCropTarget(
  keyframes: CropCenterKeyframe[],
  scenes: readonly SceneLike[],
  time: number,
): SceneCropTarget | null {
  const scene = scenes.find((s) => time >= s.startTime && time < s.endTime)
    ?? scenes.find((s) => time === s.endTime);
  if (!scene || keyframes.length === 0) return null;
  const filled = fillIntervalCropGaps(keyframes, [scene]);
  // Prefer the keyframe that starts at the playhead over one ending there.
  const keyframe = filled.find((k) => overlapsScene(k, scene) && k.time <= time && time < k.endTime)
    ?? resolveCropKeyframeAtTime(filled, time);
  if (!keyframe) return null;
  const start = Math.max(keyframe.time, scene.startTime);
  const end = Math.min(keyframe.endTime, scene.endTime);
  return end > start
    ? { keyframe, scene, start, end }
    : { keyframe, scene, start: scene.startTime, end: scene.endTime };
}

/**
 * Set `framing` on the `[range.start, range.end]` source range only. Keyframes
 * are split at the range edges, and every scene no keyframe covers first gets
 * its own keyframe, so no other scene — including one that only held the
 * edited crop across a gap — changes.
 */
export function applySceneCropFraming({ keyframes, scenes, range, framing }: {
  keyframes: CropCenterKeyframe[];
  scenes: readonly SceneLike[];
  range: { start: number; end: number };
  framing: CropFraming;
}): CropCenterKeyframe[] {
  const filled = fillIntervalCropGaps(keyframes, [...scenes, { startTime: range.start, endTime: range.end }]);
  return splitSceneCropKeyframes(filled, [{ startTime: range.start, endTime: range.end }])
    .map((keyframe) => {
      if (keyframe.time < range.start || keyframe.endTime > range.end) return keyframe;
      const next: CropCenterKeyframe = { ...keyframe, x: framing.x, y: framing.y, zoom: framing.zoom };
      if (next.zoom === undefined) delete next.zoom;
      return next;
    });
}

/** How many framing sections (keyframes, or the held crop in a gap) a scene has. */
export function countSceneCropSections(keyframes: CropCenterKeyframe[], scene: SceneLike): number {
  return fillIntervalCropGaps(keyframes, [scene]).filter((keyframe) => overlapsScene(keyframe, scene)).length;
}

/**
 * Remove the `range` framing section of `scene`: that stretch takes the
 * framing of the section before it in the same scene (the next one when it
 * opens the scene), and the boundary between them goes away. Nothing outside
 * `range` changes. Deleting used to drop the whole shot keyframe, handing
 * every scene it spanned to the previous shot. Returns `keyframes` itself
 * when the scene has no other section to merge into.
 */
export function removeSceneCropSection({ keyframes, scenes, scene, range }: {
  keyframes: CropCenterKeyframe[];
  scenes: readonly SceneLike[];
  scene: SceneLike;
  range: { start: number; end: number };
}): CropCenterKeyframe[] {
  if (countSceneCropSections(keyframes, scene) < 2) return keyframes;
  const pieces = splitSceneCropKeyframes(
    fillIntervalCropGaps(keyframes, [...scenes, scene]),
    [{ startTime: range.start, endTime: range.end }],
  );
  const inRange = (k: CropCenterKeyframe) => k.time >= range.start && k.endTime <= range.end;
  const before = pieces.find((k) => k.endTime === range.start && overlapsScene(k, scene));
  const after = before ? undefined : pieces.find((k) => k.time === range.end && overlapsScene(k, scene));
  const merged = before ? { ...before, endTime: range.end } : after ? { ...after, time: range.start } : null;
  if (!merged) return keyframes;
  return pieces
    .filter((k) => k !== before && k !== after && !inRange(k))
    .concat(merged)
    .sort((a, b) => a.time - b.time);
}
